import React from "react";
import { Badge } from "components/common/Badge";
import { Tooltip } from "@/components/common/Tooltip";
import { ConnectionTypeIcon, connectionTypeLabel } from "./ConnectionTypeIcon";
import type { ConnectionType } from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";

interface Props {
  type: ConnectionType;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  /** Optional short label rendered as a Badge under the description
   *  (e.g. "Requires Chrome / Edge / Arc" for the SQLite capability gate).
   *  When present, the tile reads as a capability-blocked state rather
   *  than the generic pending/unavailable disabled state. */
  pillLabel?: string;
  /** Long-form explanation read by screen readers when the tile is
   *  focused. Wire this to a visually-hidden span via aria-describedby
   *  to give keyboard + AT users the same path-forward copy hover users
   *  get from the title tooltip. */
  describedBy?: string;
  /** Long-form copy mirrored to the native `title` tooltip (hover) and
   *  to the visually-hidden description span (keyboard / screen reader). */
  description2?: string;
}

export const ConnectionTypeTile: React.FC<Props> = ({
  type,
  description,
  onClick,
  disabled = false,
  pillLabel,
  describedBy,
  description2,
}) => {
  const label = connectionTypeLabel(type);
  return (
    <Tooltip title={description2}>
      <button
        type="button"
        aria-disabled={disabled || undefined}
        aria-describedby={describedBy}
        onClick={() => !disabled && onClick()}
        className="flex flex-col gap-1 p-3 border border-border rounded-app bg-bg text-left cursor-pointer hover:border-accent hover:bg-list-hover aria-disabled:opacity-50 aria-disabled:hover:border-border aria-disabled:hover:bg-bg aria-disabled:cursor-not-allowed transition-colors"
        aria-label={`${label} connection`}
      >
        <ConnectionTypeIcon type={type} size={20} />
        <span className="text-fg-bold">{label}</span>
        <span className="text-fg-muted-regular-small">{description}</span>
        {pillLabel && (
          <Badge
            label={pillLabel}
            variant="warning"
            size="xs"
            uppercase={false}
          />
        )}
      </button>
    </Tooltip>
  );
};
